import React, { useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import userService from '../services/userService';
import { formatDate } from '../utils/helpers';

const ProfilePage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [savedPassengers, setSavedPassengers] = useState([]);

  useEffect(() => {
    if (!user) {
      return;
    }

    let isMounted = true;

    const loadProfileData = async () => {
      try {
        const [userData, passengers] = await Promise.all([
          userService.getUserProfile(user.uid),
          userService.getSavedPassengers(user.uid),
        ]);

        if (!isMounted) {
          return;
        }

        setProfile(userData);
        setFormData(userData || {});
        setSavedPassengers(passengers);
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    loadProfileData();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await userService.updateProfile(user.uid, {
        ...formData,
        profileComplete: true,
      });
      setProfile(formData);
      setEditing(false);
      alert('Profile updated successfully');
    } catch (error) {
      alert('Error updating profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">My Profile</h1>

        {/* Profile Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center mb-8">
            <img
              src={user.photoURL || 'https://via.placeholder.com/100'}
              alt={user.displayName}
              className="w-20 h-20 rounded-full mr-6"
            />
            <div>
              <h2 className="text-2xl font-bold">{user.displayName}</h2>
              <p className="text-gray-600">{user.email}</p>
              <span className="inline-block mt-2 bg-primary text-white px-3 py-1 rounded text-sm font-semibold">
                {profile.role?.toUpperCase() || 'PASSENGER'}
              </span>
            </div>
          </div>

          {!editing ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <p className="text-gray-600 text-sm">Phone Number</p>
                  <p className="font-semibold">{profile.phoneNumber || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Address</p>
                  <p className="font-semibold">{profile.address || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">City</p>
                  <p className="font-semibold">{profile.city || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">State</p>
                  <p className="font-semibold">{profile.state || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">ZIP Code</p>
                  <p className="font-semibold">{profile.zipCode || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Member Since</p>
                  <p className="font-semibold">{formatDate(profile.createdAt)}</p>
                </div>
              </div>

              <button
                onClick={() => setEditing(true)}
                className="bg-primary text-white px-6 py-2 rounded hover:bg-blue-700 transition"
              >
                Edit Profile
              </button>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">ZIP Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode || ''}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex-1 bg-primary text-white py-2 rounded hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setFormData(profile);
                  }}
                  className="flex-1 bg-gray-300 text-gray-900 py-2 rounded hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>

        {/* Saved Passengers */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold mb-6">Saved Passengers</h2>
          {savedPassengers.length === 0 ? (
            <p className="text-gray-600">No saved passengers yet</p>
          ) : (
            <div className="space-y-4">
              {savedPassengers.map((passenger) => (
                <div key={passenger.id} className="border border-gray-200 p-4 rounded-lg">
                  <p className="font-semibold">{passenger.name}</p>
                  <p className="text-sm text-gray-600">{passenger.category}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
